<script setup lang="ts">
import type { Color } from '@global-types/colors.ts';
import { computed, ref } from 'vue';
import Icon from '@components/Icon.vue';
import { IconBuildingsFill, IconChairFill, IconDoorFill, IconBuildingsMagic } from '@icons';
import { useFilterStore } from '@stores/useFilterStore.ts';
import { storeToRefs } from 'pinia';
import Typography from '@components/Typography.vue';
import CheckboxCard from '@pages/search/components/filters/CheckboxCard.vue';
import AggregateFilter from '@pages/search/components/filters/AggregateFilter.vue';
import { trans, transchoice } from '@tools/translation.ts';
import { OfficeType } from '@enums/officeType.ts';

const companyName = computed<string>(() => window.COMPANY_NAME);

const filterStore = useFilterStore();
const { teamSizeMin } = storeToRefs(filterStore);

const selectedOfficeTypes = ref<OfficeType[]>([]);

const toggleSelection = (value: OfficeType): void => {
  const index = selectedOfficeTypes.value.indexOf(value);

  if (index === -1) {
    selectedOfficeTypes.value.push(value);
    return;
  }

  selectedOfficeTypes.value.splice(index, 1);
};

const recommendedTypes = computed<OfficeType[]>(() => {
  if (!teamSizeMin.value) {
    return [];
  }

  if (teamSizeMin.value >= 1 && teamSizeMin.value <= 5) {
    return [ OfficeType.PRIVATE, OfficeType.OPEN_SPACE ];
  }

  if (teamSizeMin.value >= 6 && teamSizeMin.value <= 11) {
    return [ OfficeType.PRIVATE ];
  }

  return [ OfficeType.INDEPENDENT, OfficeType.PRIVATE ];
});

const isSelected = (value: OfficeType): boolean => selectedOfficeTypes.value.includes(value);

const isRecommended = (value: OfficeType): boolean => recommendedTypes.value.includes(value);

const getIconColor = (officeType: OfficeType): Color => {
  if (isSelected(officeType)) {
    return 'blue';
  }

  if (isRecommended(officeType)) {
    return 'purple';
  }

  return 'gray-dark';
};
</script>

<template>
  <div>
    <div class="p-3 lg:p-4">
      <div
        v-if="teamSizeMin !== null && teamSizeMin > 0"
        :class="[
          'mb-4 flex items-center gap-2 p-2 lg:gap-4',
          'rounded-lg',
          'bg-purple-lighter'
        ]"
      >
        <Icon
          :svg="IconBuildingsMagic"
          width="24"
          height="24"
          stroke="purple"
        />

        <div>
          <!-- eslint-disable vue/no-v-html vue/no-v-text-v-html-on-component-->
          <Typography
            variant="body-small"
            color="purple"
            class="flex h-6"
            v-html="transchoice('search.filter.office_type.panel', { count: teamSizeMin })"
          />
          <!-- eslint-enable vue/no-v-html vue/no-v-text-v-html-on-component -->

          <Typography
            variant="body-small"
            color="purple"
            class="h-6"
          >
            {{ transchoice('search.filter.office_type.panel2', { brand: companyName, count: recommendedTypes.length }) }}
          </Typography>
        </div>
      </div>

      <div class="flex flex-col gap-4 lg:flex-row">
        <CheckboxCard
          :item="{
            value: OfficeType.INDEPENDENT,
            selected: isSelected(OfficeType.INDEPENDENT),
            highlighted: isRecommended(OfficeType.INDEPENDENT)
          }"
          @checked="toggleSelection(OfficeType.INDEPENDENT)"
        >
          <template #label>
            {{ trans('search.filter.office_type.independent') }}
          </template>

          <template #icon>
            <Icon
              :svg="IconBuildingsFill"
              width="24"
              height="24"
              :stroke="getIconColor(OfficeType.INDEPENDENT)"
            />
          </template>
        </CheckboxCard>

        <CheckboxCard
          :item="{
            value: OfficeType.PRIVATE,
            selected: isSelected(OfficeType.PRIVATE),
            highlighted: isRecommended(OfficeType.PRIVATE)
          }"
          @checked="toggleSelection(OfficeType.PRIVATE)"
        >
          <template #label>
            {{ trans('search.filter.office_type.private') }}
          </template>

          <template #icon>
            <Icon
              :svg="IconDoorFill"
              width="24"
              height="24"
              :stroke="getIconColor(OfficeType.PRIVATE)"
            />
          </template>
        </CheckboxCard>

        <CheckboxCard
          :item="{
            value: OfficeType.OPEN_SPACE,
            selected: isSelected(OfficeType.OPEN_SPACE),
            highlighted: isRecommended(OfficeType.OPEN_SPACE)
          }"
          @checked="toggleSelection(OfficeType.OPEN_SPACE)"
        >
          <template #label>
            {{ trans('search.filter.office_type.open_space') }}
          </template>

          <template #icon>
            <Icon
              :svg="IconChairFill"
              width="24"
              height="24"
              :stroke="getIconColor(OfficeType.OPEN_SPACE)"
            />
          </template>
        </CheckboxCard>
      </div>
    </div>

    <AggregateFilter />
  </div>
</template>
